import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Activity, Users, Trophy, ChevronRight, Mail, Zap, Shield, Brain, ChevronLeft, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Force deployment: v3.0 - Flip cards implemented 2025-07-20

const Landing: React.FC = () => {
  const [email, setEmail] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [flippedCard, setFlippedCard] = useState<string | null>(null);
  const [currentTeamSlide, setCurrentTeamSlide] = useState(0);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setEmail('');
  };

  const toggleFlip = (value: string) => {
    setFlippedCard(flippedCard === value ? null : value);
  };

  const footballTips = [
    {
      title: "How to shoot better",
      content: "Perfect your shooting technique with AI-powered analysis that tracks your leg speed, impact angle, and follow-through."
    },
    {
      title: "How to pass better",
      content: "Enhance your passing game with real-time data on ball speed, trajectory, and weight of pass for perfect distribution."
    },
    {
      title: "How to take freekicks better",
      content: "Master set pieces with biomechanical analysis of your run-up, plant foot position, and ball contact point."
    }
  ];

  const teamPhotos = [10, 9, 7, 5, 4, 3, 2].map((num) => ({
    src: `/images/team/cresento-${num}.png`,
    alt: `Team working together ${num}`
  }));

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % footballTips.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + footballTips.length) % footballTips.length);
  };

  const nextTeamSlide = () => {
    setCurrentTeamSlide((prev) => (prev + 1) % teamPhotos.length);
  };

  const prevTeamSlide = () => {
    setCurrentTeamSlide((prev) => (prev - 1 + teamPhotos.length) % teamPhotos.length);
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  // Auto-play slideshow
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        nextSlide();
      }, 5000); // Change slide every 5 seconds
      return () => clearInterval(interval);
    }
  }, [currentSlide, isPaused]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section with Cresento Brand Colors */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        >
          <source src="/video.mp4" type="video/mp4" />
          <img src="/video-poster.jpg" alt="Cresento AI Shin Guards" className="w-full h-full object-cover" />
        </video>
        
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-cresento-primary/60 via-cresento-dark/50 to-black/70"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-cresento-secondary/20 to-cresento-accent/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-cresento-accent/20 to-cresento-secondary/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto"
          >
            {/* Gold Award Badge - Clickable */}
            <a 
              href="https://seng.hkust.edu.hk/news/20250528/three-outstanding-student-initiated-experiential-learning-projects-recognized-usel-asmpt-technology-award-2025-experiential-learning-day"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-cresento-accent/20 backdrop-blur-sm border border-yellow-500/30 rounded-full px-4 py-2 mb-6 hover:border-yellow-500/50 hover:from-yellow-500/30 hover:to-cresento-accent/30 transition-all duration-300 cursor-pointer group"
            >
              <Trophy className="w-4 h-4 text-yellow-400 group-hover:scale-110 transition-transform" />
              <span className="text-yellow-300 text-sm font-semibold">Gold Award Winner 2025</span>
            </a>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-white via-cresento-light to-cresento-accent bg-clip-text text-transparent">
              AI-Powered Smart Shin Pads
              <br />
              <span className="bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
                The Football of the Future
              </span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 mb-3 sm:mb-4 max-w-3xl mx-auto px-4 sm:px-0">
              Unlock Your Full Potential. Redefine Your Game.
            </p>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 mb-6 sm:mb-8 max-w-2xl mx-auto px-4 sm:px-0">
              Revolutionary performance analysis and injury prevention technology built into the shin pads you already wear.
            </p>

            {/* How-To Slideshow */}
            <div 
              className="relative w-full max-w-[90vw] sm:max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto mb-6 sm:mb-8 md:mb-10 lg:mb-12"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="bg-gradient-to-br from-cresento-primary/80 to-cresento-dark/60 backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8 border border-cresento-secondary/30">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                    className="text-center"
                  >
                    <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 md:mb-4 text-cresento-accent">
                      {footballTips[currentSlide].title}
                    </h3>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed px-0">
                      {footballTips[currentSlide].content}
                    </p>
                  </motion.div>
                </AnimatePresence>

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-2 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 bg-cresento-secondary/20 hover:bg-cresento-secondary/40 p-1.5 sm:p-2 rounded-full transition-all duration-300 group"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-cresento-accent group-hover:scale-110 transition-transform" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 sm:right-3 md:right-4 top-1/2 -translate-y-1/2 bg-cresento-secondary/20 hover:bg-cresento-secondary/40 p-1.5 sm:p-2 rounded-full transition-all duration-300 group"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-cresento-accent group-hover:scale-110 transition-transform" />
                </button>

                {/* Slide Indicators */}
                <div className="flex justify-center gap-1.5 sm:gap-2 mt-3 sm:mt-4 md:mt-5 lg:mt-6">
                  {footballTips.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide 
                          ? 'w-6 sm:w-8 bg-cresento-accent' 
                          : 'w-1.5 sm:w-2 bg-cresento-secondary/40 hover:bg-cresento-secondary/60'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Progress Bar */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 sm:h-1 bg-cresento-primary/40 rounded-b-lg sm:rounded-b-xl md:rounded-b-2xl overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-cresento-secondary to-cresento-accent"
                    initial={{ width: "0%" }}
                    animate={{ width: isPaused ? `${((currentSlide + 1) / footballTips.length) * 100}%` : "100%" }}
                    transition={{ duration: isPaused ? 0 : 5, ease: "linear" }}
                    onAnimationComplete={() => !isPaused && nextSlide()}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/level-up" 
                className="bg-gradient-to-r from-cresento-secondary to-cresento-accent hover:from-cresento-secondary/90 hover:to-cresento-accent/90 text-white px-8 py-4 text-lg rounded-xl font-semibold inline-flex items-center transition-all duration-300 hover:scale-105"
              >
                Sign Up for Updates <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              
              <Link 
                to="/about" 
                className="border border-cresento-secondary text-cresento-secondary hover:bg-cresento-secondary/10 px-8 py-4 text-lg rounded-xl font-semibold inline-flex items-center transition-all duration-300"
              >
                <Play className="mr-2 h-5 w-5" />
                See Our Tech in Action
              </Link>
            </div>

          </motion.div>
        </div>
      </section>

      {/* Cresento's Mission Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-dark/40 to-cresento-primary/30">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 sm:mb-12 bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Cresento's Mission
            </h2>
            
            <div className="text-left space-y-6 text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                The mission at Cresento is to <span className="text-cresento-accent font-semibold">democratize sports analytics</span>, making those elite level insights accessible to your everyday athletes, not just the highest level of athletes. It's an unfair monopoly of data driven insights.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                We are starting with football but it does not end there, from the grassroots to the highest level, in all sports, <span className="text-cresento-secondary font-semibold">every player deserves these data driven insights</span> to improve, to compete, to be seen.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                With our <span className="text-cresento-accent font-semibold">smart wearables and AI coach</span>, athletes will be able to access this data driven insights in an affordable and non complex form factor.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                viewport={{ once: true }}
                className="text-xl sm:text-2xl lg:text-3xl font-bold text-white border-l-4 border-cresento-accent pl-6 mt-8"
              >
                So any young athlete chasing a dream, Cresento can empower them to become the best versions of themselves.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cresento's Values Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-black via-cresento-primary/10 to-cresento-dark/20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Cresento's Values
            </h2>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {/* SIMPLICITY */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="flip-card-container h-64 cursor-pointer"
                style={{ perspective: '1000px' }}
                onClick={() => toggleFlip('SIMPLICITY')}
              >
                <motion.div
                  className="flip-card-inner w-full h-full relative"
                  style={{ transformStyle: 'preserve-3d' }}
                  animate={{ rotateY: flippedCard === 'SIMPLICITY' ? 180 : 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Front Face */}
                  <div 
                    className="flip-card-front absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-4">
                      SIMPLICITY
                    </h3>
                    <p className="text-gray-400 text-sm">Click to learn more</p>
                  </div>

                  {/* Back Face */}
                  <div 
                    className="flip-card-back absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/30 flex flex-col justify-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-4">
                      SIMPLICITY
                    </h3>
                    <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                      Our commitment to <span className="text-cresento-accent font-semibold">SIMPLICITY</span> in our design delivers a product that is effortless to use. But this simplicity is our greatest technological achievement. We have packed a state-of-the-art AI powered coach into a single shin pad, worn by you and measured by us.
                    </p>
                    <p className="text-gray-400 text-xs mt-4">Click to flip back</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* POTENTIAL */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="flip-card-container h-64 cursor-pointer"
                style={{ perspective: '1000px' }}
                onClick={() => toggleFlip('POTENTIAL')}
              >
                <motion.div
                  className="flip-card-inner w-full h-full relative"
                  style={{ transformStyle: 'preserve-3d' }}
                  animate={{ rotateY: flippedCard === 'POTENTIAL' ? 180 : 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Front Face */}
                  <div 
                    className="flip-card-front absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-accent/30 hover:border-cresento-accent/60 transition-all duration-300 flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cresento-accent to-cresento-secondary bg-clip-text text-transparent mb-4">
                      POTENTIAL
                    </h3>
                    <p className="text-gray-400 text-sm">Click to learn more</p>
                  </div>

                  {/* Back Face */}
                  <div 
                    className="flip-card-back absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-accent/30 flex flex-col justify-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-cresento-accent to-cresento-secondary bg-clip-text text-transparent mb-4">
                      POTENTIAL
                    </h3>
                    <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                      This allows us to unlock every athlete's true <span className="text-cresento-secondary font-semibold">POTENTIAL</span> by providing real, actionable insights and customized training plans.
                    </p>
                    <p className="text-gray-400 text-xs mt-4">Click to flip back</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* TRUST */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="flip-card-container h-64 cursor-pointer"
                style={{ perspective: '1000px' }}
                onClick={() => toggleFlip('TRUST')}
              >
                <motion.div
                  className="flip-card-inner w-full h-full relative"
                  style={{ transformStyle: 'preserve-3d' }}
                  animate={{ rotateY: flippedCard === 'TRUST' ? 180 : 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Front Face */}
                  <div 
                    className="flip-card-front absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-4">
                      TRUST
                    </h3>
                    <p className="text-gray-400 text-sm">Click to learn more</p>
                  </div>

                  {/* Back Face */}
                  <div 
                    className="flip-card-back absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/30 flex flex-col justify-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-4">
                      TRUST
                    </h3>
                    <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                      <span className="text-cresento-accent font-semibold">TRUST</span> is in the foundation of everything we do, because we are also athletes, from listening to our community to being transparent in our business core.
                    </p>
                    <p className="text-gray-400 text-xs mt-4">Click to flip back</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* COMMUNITY */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                onClick={() => toggleFlip('COMMUNITY')}
                className="flip-card-container h-64 cursor-pointer"
                style={{ perspective: '1000px' }}
              >
                <motion.div
                  className="flip-card-inner w-full h-full relative"
                  style={{ transformStyle: 'preserve-3d' }}
                  animate={{ rotateY: flippedCard === 'COMMUNITY' ? 180 : 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Front Face */}
                  <div 
                    className="flip-card-front absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-accent/30 hover:border-cresento-accent/60 transition-all duration-300 flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cresento-accent to-cresento-secondary bg-clip-text text-transparent mb-4">
                      COMMUNITY
                    </h3>
                    <p className="text-gray-400 text-xs mt-4">Click to learn more</p>
                  </div>

                  {/* Back Face */}
                  <div 
                    className="flip-card-back absolute w-full h-full bg-gradient-to-br from-cresento-accent/20 to-cresento-secondary/20 rounded-2xl p-6 sm:p-8 border border-cresento-accent/60 flex flex-col justify-center text-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <p className="text-gray-300 text-lg sm:text-xl leading-relaxed mb-4">
                      From listening to our <span className="text-cresento-secondary font-semibold">COMMUNITY</span> to being transparent in our business core, we believe in building together with our athletes.
                    </p>
                    <p className="text-gray-400 text-xs mt-4">Click to flip back</p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-primary/30 via-cresento-dark/20 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Our Story
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-8 sm:space-y-12">
            {/* The Observation */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-r from-cresento-primary/20 to-transparent p-6 sm:p-8 rounded-2xl border-l-4 border-cresento-accent">
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Cresento was born from a simple observation: while professional athletes have access to advanced performance analytics, amateur and youth players are left behind. Our founders, passionate about both technology and football, saw an opportunity to <span className="text-cresento-accent font-semibold">bridge this gap</span>.
                </p>
              </div>
            </motion.div>

            {/* The Beginning */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-l from-cresento-primary/20 to-transparent p-6 sm:p-8 rounded-2xl border-r-4 border-cresento-secondary ml-auto">
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Starting in the <span className="text-cresento-secondary font-semibold">HKUST Dream Builder Program</span>, we developed our first prototype that could track basic metrics. The response from players was overwhelming – they wanted more. This drove us to push the boundaries of what's possible, integrating AI to provide not just data, but <span className="text-cresento-accent font-semibold">actionable insights</span>. Our innovation was recognized when we won the <span className="text-cresento-accent font-semibold">Gold Award at the USEL-ASMPT Technology Award 2025</span>, beating other outstanding projects at HKUST's Experiential Learning Day.
                </p>
              </div>
            </motion.div>

            {/* The Breakthrough */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-r from-cresento-primary/20 to-transparent p-6 sm:p-8 rounded-2xl border-l-4 border-cresento-accent">
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Our breakthrough came when we realized that <span className="text-cresento-accent font-semibold">shin guards</span> – essential protective equipment worn by every player – could be the perfect platform for our technology. By embedding sensors and processors into something players already wear, we created a <span className="text-cresento-secondary font-semibold">seamless experience</span> that doesn't interfere with the game.
                </p>
              </div>
            </motion.div>

            {/* Today's Success */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-cresento-secondary/20 via-cresento-accent/20 to-cresento-primary/20 p-6 sm:p-8 rounded-2xl border border-cresento-secondary/50 text-center">
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed mb-4">
                  Today, after winning <span className="text-cresento-accent font-semibold">Unicorn Day</span> and partnering with elite clubs like <span className="text-cresento-secondary font-semibold">Inter Milan and Juventus</span>, we're proud to bring professional-level analytics to players at every level.
                </p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white">
                  From youth academies to weekend warriors, <span className="bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">Cresento is democratizing access to the tools that make champions</span>.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Milestones Achieved Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-dark/20 to-cresento-accent/10">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Milestones Achieved
            </h2>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {/* AI-Enabled Prototypes */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center mb-4">
                  <Brain className="w-6 h-6 text-cresento-accent mr-3" />
                  <h3 className="text-xl font-semibold text-white">AI-Enabled Prototypes</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Successfully developed fully integrated <span className="text-cresento-accent font-semibold">AI-enabled shin pad prototypes</span>
                </p>
              </motion.div>

              {/* Seamless Connectivity */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-accent/30 hover:border-cresento-accent/60 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center mb-4">
                  <Zap className="w-6 h-6 text-cresento-secondary mr-3" />
                  <h3 className="text-xl font-semibold text-white">Seamless Connectivity</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Established robust <span className="text-cresento-secondary font-semibold">Bluetooth (BLE) and Wi-Fi data transfer protocols</span>
                </p>
              </motion.div>

              {/* Intuitive Mobile App */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center mb-4">
                  <Activity className="w-6 h-6 text-cresento-accent mr-3" />
                  <h3 className="text-xl font-semibold text-white">Intuitive Mobile App</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Created sophisticated <span className="text-cresento-accent font-semibold">companion mobile app with exceptional UX</span>
                </p>
              </motion.div>

              {/* Positive Validation */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-accent/30 hover:border-cresento-accent/60 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center mb-4">
                  <Shield className="w-6 h-6 text-cresento-secondary mr-3" />
                  <h3 className="text-xl font-semibold text-white">Positive Validation</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Received <span className="text-cresento-secondary font-semibold">overwhelmingly positive user validation</span> from initial testing
                </p>
              </motion.div>

              {/* Market Positioning */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center mb-4">
                  <Trophy className="w-6 h-6 text-cresento-accent mr-3" />
                  <h3 className="text-xl font-semibold text-white">Market Positioning</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Positioned as <span className="text-cresento-accent font-semibold">innovative competitor to Playermaker, Soccerment, and Gengee</span>
                </p>
              </motion.div>

              {/* Professional Recognition */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-accent/30 hover:border-cresento-accent/60 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center mb-4">
                  <Users className="w-6 h-6 text-cresento-secondary mr-3" />
                  <h3 className="text-xl font-semibold text-white">Professional Recognition</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Trusted by <span className="text-cresento-secondary font-semibold">Hong Kong Premier League players and elite athletes</span>
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Team Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-accent/10 via-black to-cresento-primary/20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Meet Our Team
            </h2>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            {/* Founders Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 lg:gap-12 mb-16 max-w-3xl mx-auto">
              {/* Struan Dsouza */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="relative mb-6 mx-auto">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-cresento-secondary to-cresento-accent flex items-center justify-center group-hover:scale-105 transition-transform duration-300 border-4 border-cresento-secondary shadow-lg shadow-cresento-secondary/50">
                    <img 
                      src="/images/team/struan-dsouza.jpg" 
                      alt="Struan Dsouza"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<div class="text-white text-4xl font-bold">SD</div>';
                      }}
                    />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white group-hover:text-cresento-accent transition-colors">
                  Struan Dsouza
                </h3>
                <p className="text-cresento-secondary font-semibold mb-3 text-lg">Co-Founder & CEO</p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  Visionary entrepreneur driving the future of sports technology
                </p>
              </motion.div>

              {/* Kushaj Dwivedi */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="relative mb-6 mx-auto">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-cresento-secondary to-cresento-accent flex items-center justify-center group-hover:scale-105 transition-transform duration-300 border-4 border-cresento-secondary shadow-lg shadow-cresento-secondary/50">
                    <img 
                      src="/images/team/Kushaj.jpg" 
                      alt="Kushaj Dwivedi"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<div class="text-white text-4xl font-bold">KD</div>';
                      }}
                    />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white group-hover:text-cresento-secondary transition-colors">
                  Kushaj Dwivedi
                </h3>
                <p className="text-cresento-accent font-semibold mb-3 text-lg">Co-Founder & CTO</p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  AI/ML expert revolutionizing athletic performance analytics
                </p>
              </motion.div>
            </div>

            {/* Advisors & Consultants Row */}
            <div className="text-center mb-8">
              <h3 className="text-2xl sm:text-3xl font-bold text-cresento-accent mb-2">Advisors & Consultants</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {/* Prof. Coral Puig */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="relative mb-6 mx-auto">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-cresento-secondary to-cresento-accent flex items-center justify-center group-hover:scale-105 transition-transform duration-300 border-4 border-cresento-secondary shadow-lg shadow-cresento-secondary/50">
                    <img 
                      src="/images/team/coral.jpeg" 
                      alt="Prof. Coral Puig"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<div class="text-white text-4xl font-bold">CP</div>';
                      }}
                    />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white group-hover:text-cresento-accent transition-colors">
                  Prof. Coral Puig
                </h3>
                <p className="text-cresento-secondary font-semibold mb-3 text-lg">CMO & Marketing, Consumer Behaviour and Sales Consultant</p>
              </motion.div>

              {/* James Kwan */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="relative mb-6 mx-auto">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-cresento-secondary to-cresento-accent flex items-center justify-center group-hover:scale-105 transition-transform duration-300 border-4 border-cresento-secondary shadow-lg shadow-cresento-secondary/50">
                    <img 
                      src="/images/team/James Kwan.jpeg" 
                      alt="James Kwan"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<div class="text-white text-4xl font-bold">JK</div>';
                      }}
                    />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white group-hover:text-cresento-secondary transition-colors">
                  James Kwan
                </h3>
                <p className="text-cresento-accent font-semibold mb-3 text-lg">Executive chairman of jumpstart media, serial entrepreneur</p>
              </motion.div>

              {/* Prof. Jun Zhang */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="relative mb-6 mx-auto">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-cresento-secondary to-cresento-accent flex items-center justify-center group-hover:scale-105 transition-transform duration-300 border-4 border-cresento-secondary shadow-lg shadow-cresento-secondary/50">
                    <img 
                      src="/images/team/JunZhang.jpg" 
                      alt="Prof. Jun Zhang"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<div class="text-white text-4xl font-bold">JZ</div>';
                      }}
                    />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white group-hover:text-cresento-accent transition-colors">
                  Prof. Jun Zhang
                </h3>
                <p className="text-cresento-secondary font-semibold mb-3 text-lg">Distinguished Lecturer at HKUST IEEE Communications Society</p>
              </motion.div>

              {/* Yannick Mahé */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="relative mb-6 mx-auto">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-cresento-secondary to-cresento-accent flex items-center justify-center group-hover:scale-105 transition-transform duration-300 border-4 border-cresento-secondary shadow-lg shadow-cresento-secondary/50">
                    <img 
                      src="/images/team/Yannick Mahé .jpeg" 
                      alt="Yannick Mahé"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<div class="text-white text-4xl font-bold">YM</div>';
                      }}
                    />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white group-hover:text-cresento-secondary transition-colors">
                  Yannick Mahé
                </h3>
                <p className="text-cresento-accent font-semibold mb-3 text-lg">Board Member of AIHK</p>
              </motion.div>
            </div>

            {/* Team Working Photos Gallery */}
            <div className="text-center mb-8 mt-16">
              <h3 className="text-2xl sm:text-3xl font-bold text-cresento-secondary mb-2">Team in Action</h3>
              <p className="text-gray-300">See our team collaborating and building the future of sports technology</p>
            </div>
            
            <div 
              className="relative w-full max-w-[90vw] sm:max-w-xl md:max-w-2xl lg:max-w-4xl mx-auto mb-8"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="bg-gradient-to-br from-cresento-primary/80 to-cresento-dark/60 backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8 border border-cresento-secondary/30">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTeamSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                    className="text-center"
                  >
                    <div className="relative w-full h-64 sm:h-80 md:h-96 rounded-lg overflow-hidden">
                      <img 
                        src={teamPhotos[currentTeamSlide].src}
                        alt={teamPhotos[currentTeamSlide].alt}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `/images/team/cresento-2.png`;
                        }}
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Navigation Arrows */}
                <button
                  onClick={prevTeamSlide}
                  className="absolute left-2 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 bg-cresento-secondary/20 hover:bg-cresento-secondary/40 p-1.5 sm:p-2 rounded-full transition-all duration-300 group"
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-cresento-accent group-hover:scale-110 transition-transform" />
                </button>
                <button
                  onClick={nextTeamSlide}
                  className="absolute right-2 sm:right-3 md:right-4 top-1/2 -translate-y-1/2 bg-cresento-secondary/20 hover:bg-cresento-secondary/40 p-1.5 sm:p-2 rounded-full transition-all duration-300 group"
                  aria-label="Next photo"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-cresento-accent group-hover:scale-110 transition-transform" />
                </button>

                {/* Slide Indicators */}
                <div className="flex justify-center gap-1.5 sm:gap-2 mt-3 sm:mt-4 md:mt-5 lg:mt-6">
                  {teamPhotos.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTeamSlide(index)}
                      className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                        index === currentTeamSlide 
                          ? 'w-6 sm:w-8 bg-cresento-accent' 
                          : 'w-1.5 sm:w-2 bg-cresento-secondary/40 hover:bg-cresento-secondary/60'
                      }`}
                      aria-label={`Go to photo ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section with Winners don't settle - TEMPORARILY HIDDEN */}
      {/*
      <section className="py-8 sm:py-12 bg-gradient-to-br from-black via-cresento-primary/10 to-cresento-dark/30 relative overflow-hidden">
        {/* Animated background elements */}
        {/*<div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-40 h-40 bg-gradient-to-br from-cresento-secondary/10 to-cresento-accent/10 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-gradient-to-br from-cresento-accent/10 to-cresento-secondary/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              className="order-2 lg:order-1 text-center lg:text-left"
            >
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold bg-gradient-to-r from-cresento-secondary via-cresento-accent to-white bg-clip-text text-transparent leading-tight">
                Winners<br />Don't<br />Settle
              </h2>
              <div className="mt-4 w-24 h-1 bg-gradient-to-r from-cresento-secondary to-cresento-accent rounded-full mx-auto lg:mx-0"></div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 max-w-sm lg:max-w-md flex-shrink-0"
            >
              <div className="relative group">
                {/* Glowing border effect */}
                {/*<div className="absolute -inset-0.5 bg-gradient-to-r from-cresento-secondary to-cresento-accent rounded-2xl blur opacity-60 group-hover:opacity-100 transition duration-1000"></div>
                
                <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl">
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-auto transform group-hover:scale-105 transition-transform duration-500"
                  >
                    <source src="/winners-video.mp4" type="video/mp4" />
                    <source src="/video.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  
                  {/* Subtle overlay for premium look */}
                  {/*<div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-black/10 pointer-events-none"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      */}

      {/* Features Section with Cresento Brand Colors */}
      <section className="py-24 bg-gradient-to-b from-cresento-dark/20 to-black">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Elite Performance Features
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Cutting-edge technology designed to elevate every aspect of your game
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cresento-primary/30 to-cresento-dark/40 rounded-2xl p-8 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 hover:scale-105 group text-center"
            >
              <div className="bg-gradient-to-br from-cresento-secondary to-cresento-accent w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto">
                <Brain className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-white group-hover:text-cresento-secondary transition-colors">
                AI-Powered Insights
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Advanced machine learning algorithms analyze your every move for personalized coaching
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cresento-primary/30 to-cresento-dark/40 rounded-2xl p-8 border border-cresento-secondary/30 hover:border-cresento-accent/60 transition-all duration-300 hover:scale-105 group text-center"
            >
              <div className="bg-gradient-to-br from-cresento-accent to-cresento-secondary w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto">
                <Activity className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-white group-hover:text-cresento-accent transition-colors">
                Real-Time Analytics
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Track 50+ metrics including speed, acceleration, impact force, and technique quality
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cresento-primary/30 to-cresento-dark/40 rounded-2xl p-8 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 hover:scale-105 group text-center"
            >
              <div className="bg-gradient-to-br from-cresento-secondary to-cresento-accent w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-white group-hover:text-cresento-secondary transition-colors">
                Injury Prevention
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Monitor fatigue and biomechanics to prevent injuries before they happen
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cresento-primary/30 to-cresento-dark/40 rounded-2xl p-8 border border-cresento-secondary/30 hover:border-cresento-accent/60 transition-all duration-300 hover:scale-105 group text-center"
            >
              <div className="bg-gradient-to-br from-cresento-accent to-cresento-secondary w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-white group-hover:text-cresento-accent transition-colors">
                Personalized Feedback
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Get instant coaching tips tailored to your playing style and position
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA with Cresento Brand Colors */}
      <section className="py-24 bg-gradient-to-r from-cresento-primary/20 via-cresento-dark/20 to-black">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <Mail className="text-cresento-accent mx-auto mb-4" size={48} />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Get Exclusive Updates on Launch & Features
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Be the first to know about new features, training tips, and special offers.
            </p>
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-3 bg-cresento-primary/20 backdrop-blur border border-cresento-secondary/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-cresento-secondary"
              />
              <button 
                type="submit" 
                className="bg-gradient-to-r from-cresento-secondary to-cresento-accent hover:from-cresento-secondary/90 hover:to-cresento-accent/90 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300"
              >
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
